import L from 'leaflet'

// Trip colors alternate blue -> green so adjacent trips are distinguishable
const TRIP_COLORS = ['#2c5aa0', '#2e8b57']

function pad2(n) {
  return String(n).padStart(2, '0')
}

function toMinutes(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm || '')
  if (!m) return null
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10)
}

export default function registerDrivingReportDetail() {
  up.compiler('#reportVisuals', (el) => {
    const labels = el.dataset
    const holder = el.querySelector('#reportTripData')
    const trips = holder ? [...holder.querySelectorAll('.dr-trip-data')].map((s) => ({ ...s.dataset })) : []
    const mapEl = el.querySelector('#reportMap')
    const ttEl = el.querySelector('#reportTimetable')

    const map = L.map(mapEl, { scrollWheelZoom: false })
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(map)

    let activePins = []
    const clearPins = () => {
      activePins.forEach((p) => map.removeLayer(p))
      activePins = []
    }

    const coordPin = (latlng, colorClass, label) => {
      const icon = L.divIcon({
        className: '',
        html: `<div class="dr-pin dr-pin-${colorClass} dr-pin-sm"><span>${label}</span></div>`,
        iconSize: [38, 38],
        iconAnchor: [19, 38],
        popupAnchor: [0, -38]
      })
      const marker = L.marker(latlng, { icon }).addTo(map)
      // Right-click shows the raw coordinates; clicking them copies to the clipboard
      marker.on('contextmenu', () => {
        const text = `${latlng[0]}, ${latlng[1]}`
        const chip = document.createElement('button')
        chip.type = 'button'
        chip.className = 'dr-coords'
        const render = (icon, value) => {
          chip.innerHTML = `<i class="bi bi-${icon}"></i><span>${value}</span>`
        }
        const doCopy = () => {
          if (navigator.clipboard) navigator.clipboard.writeText(text)
          render('clipboard-check', labels.labelCopied || 'Copied')
          setTimeout(() => render('clipboard', text), 1200)
        }
        // The right-click itself copies; the chip stays clickable for re-copying
        chip.onclick = (e) => {
          L.DomEvent.stop(e)
          doCopy()
        }
        L.popup({ minWidth: 180 }).setLatLng(latlng).setContent(chip).openOn(map)
        doCopy()
      })
      return marker
    }

    const popupHtml = (t) => `
      <div class="dr-popup">
        <div class="dr-popup-header"><strong>${labels.labelTrip}${pad2(t.no)}</strong></div>
        <div>${labels.labelStart}：${t.start}　${labels.labelEnd}：${t.end}</div>
        <div>${labels.labelDistance}：${t.distance}　${labels.labelTime}：${t.time}</div>
      </div>`

    const bounds = []
    const polylines = {}
    trips.forEach((t, i) => {
      const pts = (t.route || '')
        .trim()
        .split(/\s+/)
        .map((p) => p.split(',').map(Number))
        .filter((p) => p.length === 2 && !p.some(isNaN))
      if (!pts.length) return
      pts.forEach((p) => bounds.push(p))
      const line = L.polyline(pts, { color: TRIP_COLORS[i % 2], weight: 5, opacity: 0.85 }).addTo(map)
      line.bindPopup(popupHtml(t))
      line.on('popupopen', () => {
        clearPins()
        activePins.push(coordPin(pts[0], 'blue', labels.labelStart))
        activePins.push(coordPin(pts[pts.length - 1], 'red', labels.labelEnd))
      })
      polylines[t.no] = line
    })
    if (bounds.length) {
      map.fitBounds(bounds, { padding: [30, 30] })
    } else {
      map.setView([34.72383109685586, 135.45281896187524], 11)
    }

    // 00:00-24:00 timetable; clicking a block opens the trip's popup on the map
    if (ttEl) {
      const track = document.createElement('div')
      track.className = 'dr-timetable-track'
      trips.forEach((t, i) => {
        const start = toMinutes(t.start)
        let end = toMinutes(t.end)
        if (start == null) return
        if (end == null) {
          const now = new Date()
          end = Math.min(now.getHours() * 60 + now.getMinutes(), 1440)
        }
        if (end <= start) return
        const block = document.createElement('div')
        block.className = 'dr-trip-block'
        block.style.left = `${(start / 1440) * 100}%`
        block.style.width = `${((end - start) / 1440) * 100}%`
        block.style.background = TRIP_COLORS[i % 2]
        block.title = `${labels.labelTrip}${pad2(t.no)}`
        block.onclick = () => polylines[t.no] && polylines[t.no].openPopup()
        track.appendChild(block)
      })
      const ticks = document.createElement('div')
      ticks.className = 'dr-timetable-ticks'
      for (let h = 0; h <= 24; h++) {
        const tick = document.createElement('span')
        tick.textContent = `${pad2(h)}:00`
        ticks.appendChild(tick)
      }
      ttEl.appendChild(track)
      ttEl.appendChild(ticks)
    }

    return () => map.remove()
  })
}
